'use client'

import { useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { ArrowLeft, TrendingUp } from 'lucide-react'
import { Header } from '@/components/layout/Header'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { cn, formatCurrency } from '@/lib/utils'
import type { ProductSale } from '@/lib/types'

type StarProduct = ProductSale & {
  productIds?: string[]
  inventory?: number
  createdAt?: string
  daysInInventory?: number
  daysSinceLastSale?: number
}

type SortOption = 'totalSales' | 'unitsSold' | 'rotation' | 'daysSinceLastSale' | 'daysInInventory'

const sortLabels: Record<SortOption, string> = {
  totalSales: 'Mayor contribución ($)',
  unitsSold: 'Más unidades vendidas',
  rotation: 'Mejor rotación',
  daysSinceLastSale: 'Más días sin venta',
  daysInInventory: 'Más días en tienda',
}

export default function ProductosEstrellaPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const state = location.state as {
    products?: StarProduct[]
    totalNetSales?: number
    dateLabel?: string
    productImages?: Record<string, { imageUrl?: string | null }>
  } | null

  const products = state?.products ?? []
  const totalNetSales = state?.totalNetSales ?? 0
  const dateLabel = state?.dateLabel ?? ''
  const productImages = state?.productImages ?? {}

  const [search, setSearch] = useState('')
  const [sort, setSort] = useState<SortOption>('totalSales')
  const [inventoryFilter, setInventoryFilter] = useState<'all' | 'with' | 'low' | 'zero'>('all')

  const resolveImage = (product: StarProduct): string | null => {
    if (product.imageUrl) return product.imageUrl as string
    return (
      product.productIds
        ?.map((id) => productImages[id]?.imageUrl)
        .find((v): v is string => typeof v === 'string' && v.length > 0) ?? null
    )
  }

  const filtered = useMemo(() => {
    let result = [...products]

    if (search) {
      result = result.filter((p) =>
        p.productName.toLowerCase().includes(search.toLowerCase())
      )
    }

    if (inventoryFilter === 'with') {
      result = result.filter((p) => (p.inventory ?? 0) > 0)
    } else if (inventoryFilter === 'low') {
      result = result.filter((p) => {
        const inv = p.inventory ?? 0
        return inv > 0 && inv <= 5
      })
    } else if (inventoryFilter === 'zero') {
      result = result.filter((p) => (p.inventory ?? 0) === 0)
    }

    switch (sort) {
      case 'totalSales':
        result.sort((a, b) => (b.totalSales ?? 0) - (a.totalSales ?? 0))
        break
      case 'unitsSold':
        result.sort((a, b) => (b.unitsSold ?? 0) - (a.unitsSold ?? 0))
        break
      case 'rotation':
        result.sort((a, b) => {
          const aRatio = (a.unitsSold ?? 0) / Math.max(a.inventory ?? 1, 1)
          const bRatio = (b.unitsSold ?? 0) / Math.max(b.inventory ?? 1, 1)
          return bRatio - aRatio
        })
        break
      case 'daysSinceLastSale':
        result.sort((a, b) => (b.daysSinceLastSale ?? 0) - (a.daysSinceLastSale ?? 0))
        break
      case 'daysInInventory':
        result.sort((a, b) => (b.daysInInventory ?? 0) - (a.daysInInventory ?? 0))
        break
    }

    return result
  }, [products, search, sort, inventoryFilter])

  if (!state?.products) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 flex items-center justify-center">
        <div className="text-center space-y-4">
          <p className="text-white/70">No hay datos disponibles. Genera un reporte primero desde Productos.</p>
          <Button
            onClick={() => navigate('/productos')}
            className="bg-emerald-500 hover:bg-emerald-500/90"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Ir a Productos
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900">
      <div className="mx-auto w-full max-w-[1600px] px-4 py-8 sm:px-6 lg:px-10 space-y-6">
        <Header />

        {/* Back + Title */}
        <div className="flex items-start gap-4">
          <Button
            variant="outline"
            className="flex-shrink-0 bg-white/10 text-white hover:bg-white/20"
            onClick={() => navigate('/productos')}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Volver
          </Button>
          <div>
            <h1 className="text-2xl font-semibold text-white flex items-center gap-2">
              <TrendingUp className="h-6 w-6 text-emerald-400" />
              Productos estrella
            </h1>
            <p className="text-sm text-white/50 mt-0.5">
              {dateLabel && <span>{dateLabel} — </span>}
              {products.length} productos
            </p>
          </div>
        </div>

        {/* Filters */}
        <Card className="border-white/10 bg-white/5 text-white backdrop-blur-xl">
          <CardContent className="pt-5 space-y-4">
            <input
              type="text"
              placeholder="Buscar producto..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full rounded-md border border-white/15 bg-white/10 px-3 py-2 text-sm text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />

            <div className="space-y-2">
              <p className="text-xs text-white/40 uppercase tracking-wide">Ordenar por</p>
              <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
                {(Object.keys(sortLabels) as SortOption[]).map((key) => (
                  <Button
                    key={key}
                    size="sm"
                    onClick={() => setSort(key)}
                    className={cn(
                      'flex-shrink-0',
                      sort === key
                        ? 'bg-emerald-500 text-white hover:bg-emerald-600'
                        : 'bg-white/10 text-white hover:bg-white/20'
                    )}
                  >
                    {sortLabels[key]}
                  </Button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <p className="text-xs text-white/40 uppercase tracking-wide">Inventario</p>
              <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
                {[
                  { key: 'all', label: 'Todos' },
                  { key: 'with', label: 'Con stock' },
                  { key: 'low', label: 'Stock bajo (≤5)' },
                  { key: 'zero', label: 'Agotado' },
                ] .map(({ key, label }) => (
                  <Button
                    key={key}
                    size="sm"
                    onClick={() => setInventoryFilter(key as typeof inventoryFilter)}
                    className={cn(
                      'flex-shrink-0',
                      inventoryFilter === key
                        ? 'bg-blue-500 text-white hover:bg-blue-600'
                        : 'bg-white/10 text-white hover:bg-white/20'
                    )}
                  >
                    {label}
                  </Button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Results */}
        <Card className="border-white/10 bg-white/5 text-white backdrop-blur-xl">
          <CardHeader>
            <CardTitle className="text-base">
              {filtered.length} {filtered.length !== products.length && `de ${products.length}`} productos
            </CardTitle>
            <CardDescription className="text-white/60">
              Ordenado por: {sortLabels[sort]}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {filtered.length === 0 ? (
              <p className="py-10 text-center text-sm text-white/40">
                No hay productos que coincidan con los filtros.
              </p>
            ) : (
              <ul className="divide-y divide-white/5">
                {filtered.map((product, index) => {
                  const imageUrl = resolveImage(product)
                  const imageSrc = imageUrl ?? '/placeholder.svg'
                  const percentage =
                    totalNetSales > 0 ? ((product.totalSales ?? 0) / totalNetSales) * 100 : 0
                  const inv = typeof product.inventory === 'number' ? product.inventory : 0

                  return (
                    <li key={product.productName} className="flex items-center gap-4 py-3">
                      <span className="w-7 flex-shrink-0 text-right text-sm text-white/30">
                        {index + 1}
                      </span>
                      <div className="h-12 w-12 flex-shrink-0 overflow-hidden rounded-lg bg-gray-800">
                        <img
                          src={imageSrc}
                          alt={product.productName}
                          className="h-full w-full object-cover"
                          loading="lazy"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-white truncate">{product.productName}</p>
                        <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
                          {(product.totalSales ?? 0) > 0 && (
                            <span className="text-sm text-emerald-400">
                              {formatCurrency(product.totalSales ?? 0)}
                            </span>
                          )}
                          {(product.unitsSold ?? 0) > 0 && (
                            <span className="text-sm text-white/60">
                              {product.unitsSold} uds
                            </span>
                          )}
                          {percentage > 0 && (
                            <span className="text-xs text-white/40">
                              {percentage.toFixed(1)}% del total
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex-shrink-0 text-right space-y-0.5">
                        <p
                          className={cn(
                            'text-sm font-semibold',
                            inv === 0 && 'text-white/40',
                            inv > 0 && inv <= 5 && 'text-red-400',
                            inv > 5 && inv <= 10 && 'text-amber-300',
                            inv > 10 && 'text-emerald-400'
                          )}
                        >
                          {inv} stock
                        </p>
                        {product.daysInInventory !== undefined && (
                          <p className="text-xs text-white/40">
                            {product.daysInInventory}d tienda
                          </p>
                        )}
                        {product.daysSinceLastSale !== undefined && (
                          <p
                            className={cn(
                              'text-xs',
                              product.daysSinceLastSale > 30
                                ? 'text-amber-400'
                                : 'text-white/40'
                            )}
                          >
                            {product.daysSinceLastSale}d sin venta
                          </p>
                        )}
                      </div>
                    </li>
                  )
                })}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
